import { usePageSEO } from "@/lib/use-page-seo";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useState, useCallback, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import WCLogo from "@/components/WCLogo";
import TeamGrid from "@/components/TeamGrid";
import CountryFlag from "@/components/CountryFlag";
import { useLocalPrice } from "../hooks/useLocalPrice";
import { useTranslation, Trans } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLocalePath } from "@/lib/use-locale-path";

type UserProfile = {
  email: string;
  isPaid: boolean;
  selectedTeams: number[];
  calendarToken: string;
  seasonExpired: boolean;
  seasonLabel: string;
};

type Team = {
  id: number;
  name: string;
  code: string;
  flag: string;
  group: string;
  confederation: string;
};

const BASE_URL = window.location.origin;

export default function CalendarPage() {
  usePageSEO("meta.calendar_title", "/calendar");
  const { bracket: localEstimate } = useLocalPrice(1.99, "USD");
  const { t } = useTranslation();
  const lp = useLocalePath();

  const [location] = useLocation();
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") ?? "";
  const { toast } = useToast();
  const [showTeamEditor, setShowTeamEditor] = useState(false);
  const [editingTeams, setEditingTeams] = useState<Team[]>([]);
  // Show welcome-back if user just completed renewal (either free keep or Stripe return)
  const paymentSuccess = params.get("payment") === "success" || params.get("renewed") === "true";
  const [renewalSuccess, setRenewalSuccess] = useState(paymentSuccess);
  const [renewedSeasonLabel, setRenewedSeasonLabel] = useState<string | null>(params.get("season"));
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [accountDeleted, setAccountDeleted] = useState(false);


  const { data: profile, isLoading: profileLoading } = useQuery<UserProfile>({
    queryKey: ["/api/me", token],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/me?token=${token}`);
      return res.json();
    },
    enabled: !!token,
  });

  const { data: allTeams = [], isLoading: teamsLoading } = useQuery<Team[]>({
    queryKey: ["/api/teams"],
  });

  const dataLoading = profileLoading || teamsLoading;

  // Track calendar page view (user is verified and has a valid session)
  const tracked = useRef(false);
  useEffect(() => {
    if (profile && !tracked.current) {
      (window as any).posthog?.capture('calendar_viewed', { is_paid: profile.isPaid, teams: profile.selectedTeams.length });
      (window as any).posthog?.identify(profile.email);
      tracked.current = true;
    }
  }, [profile]);

  const selectedTeamObjects = allTeams.filter((t) =>
    profile?.selectedTeams.includes(t.id)
  );

  const API_BASE = "__PORT_5000__".startsWith("__") ? "" : "__PORT_5000__";

  const updateTeamsMutation = useMutation({
    mutationFn: async (teamIds: number[]) => {
      // Use raw fetch (not apiRequest) so we can read the response body
      // before deciding whether to throw or redirect to Stripe checkout.
      // apiRequest's throwIfResNotOk discards the body on non-2xx responses.
      const res = await fetch(`${API_BASE}/api/me/teams`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, teamIds }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.requiresPayment) {
          // Redirect to Stripe checkout for upgrade — pass pending teamIds
          // so the server can store them in Stripe metadata and apply after payment
          const checkoutRes = await fetch(`${API_BASE}/api/checkout`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ calendarToken: token, pendingTeamIds: teamIds }),
          });
          const checkoutData = await checkoutRes.json();
          if (checkoutData.url) {
            window.location.href = checkoutData.url;
            return;
          }
        }
        throw new Error(data.error ?? "Failed to update teams");
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/me", token] });
      setShowTeamEditor(false);
      toast({ title: t("calendar.teams_updated"), description: t("calendar.teams_updated_desc") });
      (window as any).posthog?.capture('teams_edited', { teams: editingTeams.length });
    },
    onError: (err: any) => {
      toast({ title: t("calendar.teams_update_failed"), description: err.message, variant: "destructive" });
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/api/me/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, confirm: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error ?? "Failed to delete account");
      return data;
    },
    onSuccess: () => {
      (window as any).posthog?.capture('account_deleted');
      queryClient.clear();
      setShowDeleteModal(false);
      setAccountDeleted(true);
    },
    onError: (err: any) => {
      toast({ title: t("calendar.delete_failed"), description: err.message, variant: "destructive" });
    },
  });

  const webcalUrl = `${BASE_URL}/api/calendar/${token}.ics`.replace(/^https?/, "webcal");
  const googleCalUrl = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}`;
  const icsHttpsUrl = `${BASE_URL}/api/calendar/${token}.ics`;
  // Microsoft has two separate "subscribe from web" endpoints and no unified
  // one: outlook.live.com for personal accounts (outlook.com / hotmail / live /
  // msn) and outlook.office.com for Microsoft 365 work/school accounts. Each
  // rejects the other account type at sign-in, and the email domain can't tell
  // us which a user has, so we offer both. Personal is the primary button (our
  // audience is overwhelmingly consumer); work/school is a secondary link.
  const outlookUrl = `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(icsHttpsUrl)}`;
  const outlookWorkUrl = `https://outlook.office.com/calendar/0/addfromweb?url=${encodeURIComponent(icsHttpsUrl)}`;

  const handleEditTeams = () => {
    setEditingTeams(selectedTeamObjects);
    setShowTeamEditor(true);
  };

  const handleToggleEditTeam = (team: Team) => {
    setEditingTeams((prev) => {
      const exists = prev.find((t) => t.id === team.id);
      if (exists) return prev.filter((t) => t.id !== team.id);
      return [...prev, team];
    });
  };

  if (accountDeleted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent/15 flex items-center justify-center">
            <svg className="w-6 h-6 text-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
          </div>
          <h1 className="text-xl font-semibold text-foreground mb-2">{t("calendar.delete_done_title")}</h1>
          <p className="text-sm text-muted-foreground mb-6">{t("calendar.delete_done_body")}</p>
          <a href={lp("/")} className="inline-flex items-center px-4 py-2 rounded-lg bg-card border border-border/60 text-sm text-foreground hover:bg-card/80 transition-colors">{t("calendar.start_over")}</a>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
        {t("calendar.no_token")} <a href={lp("/")} className="ms-1 text-primary underline">{t("calendar.start_over")}</a>
      </div>
    );
  }

  if (profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <svg className="w-8 h-8 sync-ring text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12a9 9 0 11-6.219-8.56"/>
        </svg>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
        {t("calendar.not_found")} <a href={lp("/")} className="ms-1 text-primary underline">{t("calendar.start_over")}</a>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="border-b border-border/40 backdrop-blur-md bg-background/80">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <WCLogo />
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground truncate max-w-[180px] sm:max-w-none">{profile.email}</span>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Welcome back success state */}
        {renewalSuccess && !profile.seasonExpired && (
          <div className="mb-8 rounded-xl border border-accent/40 bg-accent/5 p-5 sm:p-6 animate-fade-in-up">
            <div className="flex items-start gap-3">
              <div className="shrink-0 mt-0.5">
                <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="hsl(152 55% 40%)" strokeWidth="2.5">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h2
                  className="text-lg sm:text-xl font-bold uppercase tracking-tight text-accent mb-1"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {t("calendar.welcome_back", { season: renewedSeasonLabel ?? profile.seasonLabel })}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t("calendar.welcome_back_desc")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* New season banner */}
        {profile.seasonExpired && (
          <div className="mb-8 rounded-xl border border-primary/40 bg-primary/5 p-5 sm:p-6 animate-fade-in-up">
            <div className="flex items-start gap-3">
              <div className="shrink-0 mt-0.5">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--primary))" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h2
                  className="text-lg sm:text-xl font-bold uppercase tracking-tight text-primary mb-1"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {t("calendar.new_season_title", { season: profile.seasonLabel })}
                </h2>
                <p className="text-sm text-muted-foreground mb-1">
                  {t("calendar.previous_teams_saved")}
                </p>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {selectedTeamObjects.map((t) => (
                    <span key={t.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary/10 text-xs font-medium text-foreground">
                      <CountryFlag code={t.code} emoji={t.flag} size={14} />
                      {t.name}
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={async () => {
                      const seasonForBanner = profile.seasonLabel;
                      try {
                        const res = await fetch(`${API_BASE}/api/renew`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ token }),
                        });
                        const data = await res.json();
                        if (data.requiresPayment) {
                          const checkoutRes = await fetch(`${API_BASE}/api/checkout`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ calendarToken: token, pendingTeamIds: data.teamIds }),
                          });
                          const checkoutData = await checkoutRes.json();
                          if (checkoutData.url) {
                            window.location.href = checkoutData.url;
                            return;
                          }
                        }
                        if (data.success || data.renewed) {
                          setRenewedSeasonLabel(seasonForBanner);
                          setRenewalSuccess(true);
                          queryClient.invalidateQueries({ queryKey: ["/api/me", token] });
                          (window as any).posthog?.capture('season_renewed', { method: 'keep_teams', teams: selectedTeamObjects.length });
                        }
                      } catch (err) {
                        toast({ title: t("errors.something_went_wrong"), description: t("errors.please_try_again"), variant: "destructive" });
                      }
                    }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity"
                  >
                    {t("calendar.keep_teams")}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      setEditingTeams(selectedTeamObjects);
                      setShowTeamEditor(true);
                    }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-border/60 bg-card text-sm font-medium text-foreground hover:bg-card/80 transition-colors"
                  >
                    {t("calendar.change_teams")}
                  </button>
                </div>
                {selectedTeamObjects.length >= 2 && (
                  <p className="text-xs text-muted-foreground mt-3">
                    {t("calendar.keep_teams_payment", { count: selectedTeamObjects.length, localEstimate: localEstimate ? ` ${localEstimate}` : "" })}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Success header */}
        <div className="mb-10 animate-fade-in-up">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="hsl(152 55% 40%)" strokeWidth="2.5">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
            </div>
            <span className="text-sm text-accent font-medium">{t("calendar.ready_badge")}</span>
          </div>
          <h1
            className="text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-tight mb-2"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            data-testid="calendar-heading"
          >
            {t("calendar.heading")}
          </h1>
          <p className="text-muted-foreground text-base">
            {t("calendar.subtitle")}
          </p>
        </div>

        {/* Selected teams */}
        <section className="mb-8" data-testid="selected-teams-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t("calendar.your_teams")}</h2>
            <Button variant="ghost" size="sm" onClick={handleEditTeams} className="text-xs text-muted-foreground hover:text-foreground">
              {t("calendar.edit_teams")}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {dataLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <svg className="w-4 h-4 sync-ring" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 11-6.219-8.56"/>
                </svg>
                {t("calendar.loading_teams")}
              </div>
            ) : selectedTeamObjects.length === 0 && !profile.seasonExpired ? (
              <div
                className="w-full rounded-xl border border-dashed border-primary/40 bg-primary/5 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between"
                data-testid="no-teams-empty-state"
              >
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">{t("calendar.no_teams_title")}</p>
                  <p className="text-sm text-muted-foreground">{t("calendar.no_teams_body")}</p>
                </div>
                <Button
                  onClick={handleEditTeams}
                  className="bg-primary text-primary-foreground font-semibold whitespace-nowrap"
                  data-testid="pick-teams-cta"
                >
                  {t("calendar.pick_teams")}
                </Button>
              </div>
            ) : selectedTeamObjects.map((t) => (
              <div
                key={t.id}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-primary/30 bg-primary/5 text-sm"
                data-testid={`subscribed-team-${t.id}`}
              >
                <CountryFlag code={t.code} emoji={t.flag} size={18} />
                <span className="font-medium text-foreground">{t.name}</span>
                <span className="text-muted-foreground text-xs">{t.code}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Calendar subscribe buttons */}
        <section className="mb-10" data-testid="subscribe-section">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">{t("calendar.add_to_calendar")}</h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {/* Apple Calendar */}
            <a
              href={webcalUrl}
              onClick={() => (window as any).posthog?.capture('calendar_added', { app: 'apple' })}
              className="flex items-center gap-3 p-4 rounded-xl border border-border/60 bg-card hover:bg-card/80 transition-colors hover-elevate"
              data-testid="subscribe-apple"
            >
              <img src="/apple-calendar.png" alt="Apple Calendar" width="36" height="36" className="brightness-0 invert shrink-0" />
              <div>
                <div className="text-sm font-semibold">{t("calendar.apple_calendar")}</div>
                <div className="text-xs text-muted-foreground">{t("calendar.apple_hint")}</div>
              </div>
            </a>

            {/* Google Calendar */}
            <a
              href={googleCalUrl}
              onClick={() => (window as any).posthog?.capture('calendar_added', { app: 'google' })}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 rounded-xl border border-border/60 bg-card hover:bg-card/80 transition-colors hover-elevate"
              data-testid="subscribe-google"
            >
              <img src="/google-calendar.svg" alt="Google Calendar" width="36" height="36" className="shrink-0" />
              <div>
                <div className="text-sm font-semibold">{t("calendar.google_calendar")}</div>
                <div className="text-xs text-muted-foreground">{t("calendar.google_hint")}</div>
              </div>
            </a>

            {/* Outlook */}
            <a
              href={outlookUrl}
              onClick={() => (window as any).posthog?.capture('calendar_added', { app: 'outlook' })}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 rounded-xl border border-border/60 bg-card hover:bg-card/80 transition-colors hover-elevate"
              data-testid="subscribe-outlook"
            >
              <img src="/outlook.svg" alt="Outlook" width="36" height="36" className="shrink-0" />
              <div>
                <div className="text-sm font-semibold">{t("calendar.outlook")}</div>
                <div className="text-xs text-muted-foreground">{t("calendar.outlook_hint")}</div>
              </div>
            </a>
          </div>

          {/* Work/school (Microsoft 365) accounts can't use the consumer
              outlook.live.com endpoint, so offer the office.com flow too. */}
          <p className="text-xs text-muted-foreground">
            {t("calendar.outlook_work_prompt")}{" "}
            <a
              href={outlookWorkUrl}
              onClick={() => (window as any).posthog?.capture('calendar_added', { app: 'outlook_work' })}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
              data-testid="subscribe-outlook-work"
            >
              {t("calendar.outlook_work_link")}
            </a>
          </p>
        </section>

        {/* Update frequency + Share side-by-side on desktop, stacked on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="rounded-xl border border-border/30 bg-card/40 p-5 flex gap-4">
          <div className="shrink-0 mt-0.5">
            <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="hsl(152 55% 40%)" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v6l4 2"/>
              </svg>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">{t("calendar.update_frequency_title")}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              <span dangerouslySetInnerHTML={{ __html: t("calendar.update_frequency_body") }} />
            </p>
          </div>
        </div>

        {/* Share with friends */}
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-5">
          <h3
            className="text-sm font-bold uppercase tracking-wider text-primary mb-3"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            {t("calendar.share_heading")}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t("calendar.share_body")}
          </p>
          <div className="flex flex-wrap gap-2">
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("Just set up auto-updating World Cup 2026 matches in my calendar. Picked my teams, now it syncs fixture updates and knockout matches on its own. wccalendar.com")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border/60 text-sm text-foreground hover:bg-card/80 transition-colors"
              data-testid="share-twitter"
              onClick={() => (window as any).posthog?.capture('share_clicked', { platform: 'x' })}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              {t("calendar.share_twitter")}
            </a>
            <a
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent("Just added auto-updating World Cup 2026 matches to my calendar. Picked my teams and it syncs fixture changes and knockout matches on its own. Have a look: wccalendar.com")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border/60 text-sm text-foreground hover:bg-card/80 transition-colors"
              data-testid="share-whatsapp"
              onClick={() => (window as any).posthog?.capture('share_clicked', { platform: 'whatsapp' })}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
              {t("calendar.share_whatsapp")}
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://wccalendar.com")}&quote=${encodeURIComponent("Auto-updating World Cup 2026 match calendar. Pick your teams, scores and fixture changes sync automatically.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border/60 text-sm text-foreground hover:bg-card/80 transition-colors"
              onClick={() => (window as any).posthog?.capture('share_clicked', { platform: 'facebook' })}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              {t("calendar.share_facebook")}
            </a>
            <a
              href={`sms:?&body=${encodeURIComponent("Check out this World Cup 2026 calendar that auto-updates with scores and fixture changes: https://wccalendar.com")}`}
              className="inline-flex md:hidden items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border/60 text-sm text-foreground hover:bg-card/80 transition-colors"
              onClick={() => (window as any).posthog?.capture('share_clicked', { platform: 'sms' })}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              {t("calendar.share_sms")}
            </a>
            <a
              href={`mailto:?subject=${encodeURIComponent("World Cup 2026 calendar that auto-updates")}&body=${encodeURIComponent("Found this handy tool that adds World Cup 2026 matches to your calendar. You pick your teams and it keeps everything updated automatically (scores, rescheduled matches, knockout rounds). Have a look: https://wccalendar.com")}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border/60 text-sm text-foreground hover:bg-card/80 transition-colors"
              onClick={() => (window as any).posthog?.capture('share_clicked', { platform: 'email' })}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              {t("calendar.share_email")}
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText("https://wccalendar.com");
                (window as any).posthog?.capture('share_clicked', { platform: 'copy_link' });
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border/60 text-sm text-foreground hover:bg-card/80 transition-colors cursor-pointer"
              data-testid="share-copy"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              {t("calendar.share_copy")}
            </button>
          </div>
        </div>
        </div>

        {/* Manage sign-in */}
        <div className="text-center pt-4 border-t border-border/30">
          <p className="text-sm text-muted-foreground max-w-md mx-auto text-pretty">
            <Trans i18nKey="calendar.manage_hint" components={{ 1: <a href={lp("/")} className="text-primary hover:underline" /> }} />
          </p>
        </div>

        {/* Danger zone: self-service account + data deletion (GDPR) */}
        <div className="pt-6 mt-2 border-t border-border/30 text-center">
          <button
            onClick={() => { setDeleteConfirmText(""); setShowDeleteModal(true); }}
            className="text-xs text-muted-foreground/70 hover:text-destructive underline underline-offset-2 transition-colors"
            data-testid="delete-account"
          >
            {t("calendar.delete_account")}
          </button>
        </div>

        {/* Delete-account confirmation modal (GDPR erasure) */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 bg-background/90 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto">
            <div className="w-full max-w-md bg-card border border-destructive/40 rounded-2xl p-6 sm:p-8">
              <h2 className="text-lg font-semibold text-foreground mb-2">{t("calendar.delete_modal_title")}</h2>
              <p className="text-sm text-muted-foreground mb-4">{t("calendar.delete_modal_body")}</p>
              <ul className="text-sm text-muted-foreground mb-5 space-y-1 list-disc ps-5">
                <li>{t("calendar.delete_modal_point_email")}</li>
                <li>{t("calendar.delete_modal_point_feed")}</li>
                <li>{t("calendar.delete_modal_point_permanent")}</li>
              </ul>
              <label className="block text-xs text-muted-foreground mb-2">
                <Trans i18nKey="calendar.delete_modal_confirm_label" components={{ 1: <span className="font-semibold text-foreground" /> }} />
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                autoComplete="off"
                className="w-full mb-5 px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-destructive/40"
                data-testid="delete-confirm-input"
              />
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteAccountMutation.isPending}
                >
                  {t("calendar.delete_modal_cancel")}
                </Button>
                <Button
                  className="flex-1 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => deleteAccountMutation.mutate()}
                  disabled={deleteConfirmText.trim().toUpperCase() !== t("calendar.delete_modal_confirm_word") || deleteAccountMutation.isPending}
                  data-testid="delete-confirm-button"
                >
                  {deleteAccountMutation.isPending ? t("calendar.delete_modal_deleting") : t("calendar.delete_modal_confirm_button")}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Team editor modal */}
        {showTeamEditor && (
          <div className="fixed inset-0 z-50 bg-background/90 backdrop-blur-sm flex items-start justify-center pt-16 px-4 pb-4 overflow-y-auto">
            <div className="w-full max-w-4xl bg-card border border-border rounded-2xl p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {t("calendar.edit_modal_heading")}
                </h2>
                <Button variant="ghost" size="icon" onClick={() => setShowTeamEditor(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </Button>
              </div>

              {!profile.isPaid && editingTeams.length >= 2 && (
                <div className="mb-4 p-3 rounded-lg bg-primary/10 border border-primary/30 text-sm text-primary">
                  {t("calendar.edit_multi_warning", { count: editingTeams.length, localEstimate: localEstimate ? ` ${localEstimate}` : "" })}
                </div>
              )}

              <TeamGrid
                teams={allTeams}
                selectedTeams={editingTeams}
                onToggle={handleToggleEditTeam}
                searchQuery=""
              />

              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowTeamEditor(false)} className="flex-1">
                  {t("calendar.cancel")}
                </Button>
                <Button
                  onClick={() => updateTeamsMutation.mutate(editingTeams.map((t) => t.id))}
                  disabled={editingTeams.length === 0 || updateTeamsMutation.isPending}
                  className="flex-1 bg-primary text-primary-foreground font-semibold"
                  data-testid="save-teams-btn"
                >
                  {updateTeamsMutation.isPending
                    ? t("calendar.saving")
                    : !profile.isPaid && editingTeams.length >= 2
                      ? t("calendar.save_teams_paid")
                      : t("calendar.save_teams")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
